// Screen: Waiting room (guest in attesa di ammissione)

import { icon } from './icons.js';
import { Sounds } from './sounds.js';
import type { LobbyResult } from './lobby.js';

export interface WaitingProps {
  signaling: LobbyResult['signaling'];
  joinResp: LobbyResult['joinResp'];
  devices: LobbyResult['devices'];
  prefs: LobbyResult['prefs'];
  micStream: MediaStream | null;
  camStream: MediaStream | null;
  authToken: string;
  roomId: string;
  roomLabel: string;
  apiOrigin: string;
}

interface AdmittedPayload {
  routerRtpCapabilities?: unknown;
  peers?: { id: string; displayName: string; role: 'host' | 'member' | 'guest' }[];
  existingProducers?: { peerId: string; producerId: string; kind: string; source: string }[];
  recording?: { id: string; startedBy: string; startedAt: number } | null;
  existingFiles?: NonNullable<LobbyResult['joinResp']['existingFiles']>;
}

interface Callbacks {
  onAdmitted: (payload: AdmittedPayload) => void;
  onRejected: (reason?: string) => void;
  onCancel: () => void;
  onConnectionLost: () => void;
}

export async function mountWaiting(root: HTMLElement, props: WaitingProps, cb: Callbacks): Promise<void> {
  const roomLabel = props.joinResp.roomLabel ?? 'Riunione';
  root.innerHTML = `
    <div class="waiting-screen">
      <div class="waiting-card">
        <div class="waiting-badge">${icon('clock')}</div>
        <h1>Sei in sala d'attesa</h1>
        <p>L'host sarà avvisato del tuo arrivo. Resta su questa pagina, ti faremo entrare al più presto.</p>
        <div class="waiting-meeting">
          <span class="label">Riunione</span>
          <strong>${escape(roomLabel)}</strong>
        </div>
        <div class="spinner-pulse"><span></span><span></span><span></span></div>
        <button class="btn-ghost-dark" id="cancel-wait">${icon('x', 'icon icon-sm')} Annulla</button>
        <div class="waiting-foot">
          <span>${icon('shield', 'icon icon-sm')} Connessione cifrata</span>
          <span>${icon('wifi', 'icon icon-sm')} Rete pronta</span>
        </div>
      </div>
    </div>
  `;

  let finished = false;
  root.querySelector<HTMLButtonElement>('#cancel-wait')!.addEventListener('click', () => {
    finished = true;
    cleanupStreams();
    cb.onCancel();
  });

  props.signaling.on('admitted', (n) => {
    finished = true;
    Sounds.join();
    cb.onAdmitted({
      routerRtpCapabilities: n.routerRtpCapabilities,
      peers: n.peers as { id: string; displayName: string; role: 'host' | 'member' | 'guest' }[] | undefined,
      existingProducers: n.existingProducers as { peerId: string; producerId: string; kind: string; source: string }[] | undefined,
      recording: (n.recording as { id: string; startedBy: string; startedAt: number } | null | undefined) ?? null,
      existingFiles: n.existingFiles as NonNullable<LobbyResult['joinResp']['existingFiles']> | undefined,
    });
  });
  props.signaling.on('kicked', (n) => {
    finished = true;
    cleanupStreams();
    cb.onRejected(((n.reason as string) === 'denied') ? 'L\'host ha rifiutato la tua richiesta.' : `Rimosso: ${n.reason ?? 'motivo non specificato'}`);
  });
  props.signaling.on('roomClosed', () => {
    finished = true;
    cleanupStreams();
    cb.onRejected('La riunione è stata chiusa dall\'host.');
  });
  props.signaling.onClose(() => {
    if (finished) return;
    cleanupStreams();
    cb.onConnectionLost();
  });

  function cleanupStreams() {
    props.micStream?.getTracks().forEach((t) => t.stop());
    props.camStream?.getTracks().forEach((t) => t.stop());
  }
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
